import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { networkService } from '../services/NetworkService';
import { offlineModeManager } from '../services/offline/OfflineModeManager';

interface OfflineContextType {
  isOffline: boolean;
  isMobile: boolean;
  isOfflineModeEnabled: boolean;
  shouldBypassLogin: boolean;
  offlineStatus: any;
  toggleOfflineMode: () => void;
}

const OfflineContext = createContext<OfflineContextType>({
  isOffline: false,
  isMobile: false,
  isOfflineModeEnabled: false,
  shouldBypassLogin: false,
  offlineStatus: null,
  toggleOfflineMode: () => {},
});

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isOffline, setIsOffline] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [offlineStatus, setOfflineStatus] = useState<any>(null);

  useEffect(() => {
    // Detect platform
    const platform = Capacitor.getPlatform();
    setIsMobile(platform === 'ios' || platform === 'android');

    // Listen to network changes
    const unsubscribeNetwork = networkService.addListener((status) => {
      setIsOffline(!status.connected);
    });

    // Listen to offline mode changes
    const unsubscribeOffline = offlineModeManager.addListener((status) => {
      setOfflineStatus(status);
    });

    // Get initial states
    const networkStatus = networkService.getCurrentStatus();
    setIsOffline(!networkStatus?.connected);
    setOfflineStatus(offlineModeManager.getCurrentStatus());

    return () => {
      unsubscribeNetwork();
      unsubscribeOffline();
    };
  }, []);


  const isOfflineModeEnabled = (isMobile && isOffline) || offlineStatus?.isOfflineMode || false;
  const shouldBypassLogin = isOfflineModeEnabled;

  const toggleOfflineMode = () => {
    offlineModeManager.manualUpdateOfflineMode(!isOfflineModeEnabled);
  };

  return (
    <OfflineContext.Provider
      value={{
        isOffline,
        isMobile,
        isOfflineModeEnabled,
        shouldBypassLogin,
        offlineStatus,
        toggleOfflineMode,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};